import { useState, useTransition } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Barcode as BarcodeIcon,
  CheckCircle2,
  Copy,
  EyeOff,
  FileSpreadsheet,
  Package,
  RotateCcw,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import {
  Badge,
  Button,
  EmptyState,
  Gtin,
  Loading,
  PageHeader,
  Panel,
  PanelBody,
} from '../ui/index.js';
import { fetchJson } from '../lib/fetch-json.js';
import { money, stamp, toCapitalizedCase } from '../lib/format.js';

interface DecisionProduct {
  readonly sku: string | null;
  readonly name: string;
  readonly brand: string | null;
  readonly images: readonly string[];
  readonly purchasePriceHt?: number | null;
  readonly barcodeRaw?: string | null;
  readonly eanRaw?: string | null;
  readonly gtinCanonical?: string | null;
  readonly isVisible?: boolean;
}

interface ApprovedDecisionItem {
  readonly id: number;
  readonly productId: number;
  readonly gtin: string | null;
  readonly status: string;
  readonly algoVersion: string;
  readonly decidedBy: string | null;
  readonly decidedAt: string;
  readonly product: DecisionProduct;
}

interface PaginatedApprovedResponse {
  readonly items: readonly ApprovedDecisionItem[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  readonly totalPages: number;
}

interface BrandRoster {
  readonly brands: readonly { readonly brand: string | null; readonly count: number }[];
  readonly total: number;
}

export function WorkbenchApprovedPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [, startTransition] = useTransition();

  // Filters state
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [sortBy, setSortBy] = useState<'recent' | 'oldest' | 'name' | 'brand' | 'price'>('recent');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [actionFeedback, setActionFeedback] = useState<string | null>(null);

  // Fetch approved brands for the brand filter
  const brandsQuery = useQuery({
    queryKey: ['operator-brands', 'approved'],
    queryFn: () => fetchJson<BrandRoster>('/api/v1/review/operator-brands?status=approved'),
  });

  // Fetch paginated approved decisions
  const approvedQuery = useQuery({
    queryKey: ['decisions', 'approved', appliedSearch, selectedBrand, sortBy, page, pageSize],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('status', 'approved');
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      if (appliedSearch.trim() !== '') params.set('q', appliedSearch.trim());
      if (selectedBrand.trim() !== '') params.set('brand', selectedBrand.trim());
      params.set('sortBy', sortBy);
      return fetchJson<PaginatedApprovedResponse>(`/api/v1/review/decisions?${params.toString()}`);
    },
  });

  // Reopen mutation (returns product to review queue)
  const reopenMutation = useMutation({
    mutationFn: (productId: number) =>
      fetchJson<{ status: string }>('/api/v1/review/reopen', {
        method: 'POST',
        body: JSON.stringify({ productId }),
      }),
    onSuccess: () => {
      setActionFeedback('Produit rétabli dans la file d\'attente GTIN avec succès.');
      void queryClient.invalidateQueries({ queryKey: ['decisions'] });
      void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['tab-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['workbench-tab-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['operator-brands'] });
    },
    onError: (err) => {
      setActionFeedback(`Erreur lors du rétablissement : ${err instanceof Error ? err.message : String(err)}`);
    },
  });

  // Move to pack mutation
  const setOperatorStatusMutation = useMutation({
    mutationFn: (input: { productId: number; value: 'pack' | 'hide' | 'none' }) =>
      fetchJson<{ ok: boolean }>(`/api/v1/products/${input.productId}/operator-status`, {
        method: 'POST',
        body: JSON.stringify({ value: input.value }),
      }),
    onSuccess: (_, variables) => {
      setActionFeedback(
        variables.value === 'pack'
          ? 'Produit passé en Pack (hors matching).'
          : 'Produit masqué sur WooCommerce.',
      );
      void queryClient.invalidateQueries({ queryKey: ['decisions'] });
      void queryClient.invalidateQueries({ queryKey: ['tab-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['workbench-tab-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['operator-brands'] });
    },
    onError: (err) => {
      setActionFeedback(`Erreur : ${err instanceof Error ? err.message : String(err)}`);
    },
  });

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    startTransition(() => {
      setAppliedSearch(search);
      setPage(1);
    });
  };

  const handleClearSearch = () => {
    setSearch('');
    startTransition(() => {
      setAppliedSearch('');
      setPage(1);
    });
  };

  const handleBrandChange = (brand: string) => {
    startTransition(() => {
      setSelectedBrand(brand);
      setPage(1);
    });
  };

  const handleSortChange = (newSort: typeof sortBy) => {
    startTransition(() => {
      setSortBy(newSort);
      setPage(1);
    });
  };

  const handlePageSizeChange = (newSize: number) => {
    startTransition(() => {
      setPageSize(newSize);
      setPage(1);
    });
  };

  const handleExportCsv = () => {
    const items = approvedQuery.data?.items ?? [];
    if (items.length === 0) return;

    const headers = ['ID', 'SKU', 'Nom', 'Marque', 'EAN Approuvé', 'Date Décision', 'Opérateur', 'Prix Achat HT'];
    const rows = items.map((d) => [
      d.productId,
      d.product.sku ?? '',
      `"${(d.product.name ?? '').replace(/"/g, '""')}"`,
      `"${(d.product.brand ?? '').replace(/"/g, '""')}"`,
      d.gtin ?? '',
      d.decidedAt ? new Date(d.decidedAt).toLocaleDateString('fr-FR') : '',
      `"${(d.decidedBy ?? '').replace(/"/g, '""')}"`,
      d.product.purchasePriceHt ?? '',
    ]);

    const csvContent = '\uFEFF' + [headers.join(';'), ...rows.map((r) => r.join(';'))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `produits-approuves-page-${page}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setActionFeedback(`Fichier CSV téléchargé avec succès (${items.length} lignes).`);
  };

  const data = approvedQuery.data;
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Produits Approuvés (Couche 1)"
        subtitle="Historique des décisions GTIN validées par les opérateurs. Les fiches avec code-barres approuvé sont éligibles pour le Final EAN Match (Couche 2) et l'export Sage 100c."
        meta={[
          { label: 'Total approuvés', value: `${total.toLocaleString('fr-FR')} produits` },
          { label: 'Page actuelle', value: `${page} / ${totalPages}` },
          { label: 'Affichés sur page', value: `${items.length} produits` },
        ]}
        actions={
          <Button
            type="button"
            variant="soft"
            size="sm"
            onClick={handleExportCsv}
            disabled={items.length === 0}
            icon={<FileSpreadsheet className="h-4 w-4" />}
            title="Exporter les produits approuvés affichés au format CSV"
          >
            Exporter CSV
          </Button>
        }
      />

      {actionFeedback && (
        <div className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3 text-xs text-ink shadow-2xs">
          <span>{actionFeedback}</span>
          <button
            type="button"
            onClick={() => setActionFeedback(null)}
            className="text-ink-soft hover:text-ink"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Filters Bar */}
      <Panel>
        <PanelBody className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {/* Search form */}
            <form onSubmit={handleSearchSubmit} className="flex flex-1 min-w-[260px] max-w-md items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-ghost" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher par nom, SKU, EAN approuvé…"
                  className="w-full rounded-md border border-border bg-paper pl-9 pr-8 py-1.5 text-xs text-ink placeholder:text-ink-ghost focus:border-herb-500 focus:outline-hidden"
                />
                {search && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-ghost hover:text-ink"
                    title="Effacer la recherche"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <Button type="submit" variant="soft" size="sm">
                Filtrer
              </Button>
            </form>

            {/* Brand filter */}
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-3.5 w-3.5 text-ink-soft" />
              <select
                value={selectedBrand}
                onChange={(e) => handleBrandChange(e.target.value)}
                className="rounded-md border border-border bg-paper px-2.5 py-1.5 text-xs text-ink focus:border-herb-500 focus:outline-hidden"
              >
                <option value="">Toutes les marques ({brandsQuery.data?.total ?? '…'})</option>
                {brandsQuery.data?.brands.map((b) => (
                  <option key={b.brand ?? '__none__'} value={b.brand ?? '__NOBRAND__'}>
                    {b.brand ?? 'Sans marque'} ({b.count})
                  </option>
                ))}
              </select>
            </div>

            {/* Sort & PageSize */}
            <div className="flex items-center gap-2">
              <select
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value as typeof sortBy)}
                className="rounded-md border border-border bg-paper px-2.5 py-1.5 text-xs text-ink focus:border-herb-500 focus:outline-hidden"
              >
                <option value="recent">Plus récents d&apos;abord</option>
                <option value="oldest">Plus anciens d&apos;abord</option>
                <option value="name">Nom (A → Z)</option>
                <option value="brand">Marque (A → Z)</option>
                <option value="price">Prix d&apos;achat décroissant</option>
              </select>

              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="rounded-md border border-border bg-paper px-2.5 py-1.5 text-xs text-ink focus:border-herb-500 focus:outline-hidden"
              >
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
            </div>
          </div>
        </PanelBody>
      </Panel>

      {/* Main List */}
      {approvedQuery.isLoading ? (
        <Loading label="Chargement des produits approuvés…" />
      ) : items.length === 0 ? (
        <EmptyState
          title="Aucun produit approuvé trouvé"
          hint={
            appliedSearch || selectedBrand
              ? 'Aucun résultat ne correspond à vos filtres actuels. Modifiez la recherche ou la marque.'
              : 'Aucun produit n\'a encore été approuvé dans la file de décision.'
          }
          action={
            appliedSearch || selectedBrand ? (
              <Button
                type="button"
                variant="soft"
                size="sm"
                onClick={() => {
                  setSearch('');
                  setAppliedSearch('');
                  setSelectedBrand('');
                  setPage(1);
                }}
              >
                Réinitialiser les filtres
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const prod = item.product;
            const isReopening = reopenMutation.isPending && reopenMutation.variables === item.productId;
            const isPacking =
              setOperatorStatusMutation.isPending &&
              setOperatorStatusMutation.variables?.productId === item.productId;

            return (
              <div
                key={item.id}
                className="rounded-xl border border-border bg-card p-4 shadow-2xs hover:border-border-soft transition-all"
              >
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  {/* Left: Product Thumbnail & Details */}
                  <div className="flex items-center gap-3.5 min-w-0 flex-1">
                    {prod.images && prod.images[0] ? (
                      <img
                        src={prod.images[0]}
                        alt={prod.name}
                        className="h-16 w-16 rounded-lg border border-border bg-white object-contain p-1 shrink-0"
                      />
                    ) : (
                      <div className="h-16 w-16 rounded-lg border border-border bg-cream/40 flex items-center justify-center shrink-0 text-ink-ghost">
                        <BarcodeIcon className="h-6 w-6 opacity-30" />
                      </div>
                    )}

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        {prod.brand && (
                          <Badge tone="muted" className="font-semibold">
                            {prod.brand}
                          </Badge>
                        )}
                        {prod.sku && <span className="font-mono text-xs text-ink-soft">SKU: {prod.sku}</span>}
                        <Badge tone="good">
                          <span className="flex items-center gap-1 font-semibold">
                            <CheckCircle2 className="h-3 w-3" />
                            Approuvé Couche 1
                          </span>
                        </Badge>
                      </div>

                      <h3 className="mt-1 text-sm font-bold text-ink leading-snug truncate" title={prod.name}>
                        {toCapitalizedCase(prod.name)}
                      </h3>

                      <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-soft">
                        <div className="flex items-center gap-1.5">
                          <span className="text-ink-faint">GTIN Validé :</span>
                          {item.gtin ? (
                            <span className="inline-flex items-center gap-1 rounded bg-herb-50 text-herb-900 border border-herb-200 px-2 py-0.5 font-mono text-xs font-bold">
                              <Gtin gtin={item.gtin} showBarcode={false} />
                              <button
                                type="button"
                                onClick={() => navigator.clipboard.writeText(item.gtin ?? '')}
                                className="text-herb-600 hover:text-herb-900 ml-0.5"
                                title="Copier le code GTIN"
                              >
                                <Copy className="h-3 w-3" />
                              </button>
                            </span>
                          ) : (
                            <span className="font-mono text-ink-ghost">Aucun</span>
                          )}
                        </div>

                        {prod.purchasePriceHt !== null && prod.purchasePriceHt !== undefined && (
                          <div>
                            <span className="text-ink-faint">Achat HT : </span>
                            <span className="font-bold text-ink">{money(prod.purchasePriceHt)}</span>
                          </div>
                        )}

                        <div>
                          <span className="text-ink-faint">Validé par : </span>
                          <span className="font-medium text-ink">{item.decidedBy || 'Admin'}</span>
                          {item.decidedAt && (
                            <span className="text-ink-faint ml-1">le {stamp(item.decidedAt)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right: Actions */}
                  <div className="flex flex-wrap items-center gap-2 shrink-0 self-end sm:self-center">
                    <Button
                      type="button"
                      variant="soft"
                      size="sm"
                      onClick={() => reopenMutation.mutate(item.productId)}
                      disabled={isReopening}
                      icon={<RotateCcw className="h-3.5 w-3.5 text-amber-600" />}
                      title="Annuler l'approbation et renvoyer le produit dans la file d'attente GTIN"
                    >
                      {isReopening ? 'Rétablissement…' : 'Rétablir'}
                    </Button>

                    <Button
                      type="button"
                      variant="soft"
                      size="sm"
                      onClick={() => setOperatorStatusMutation.mutate({ productId: item.productId, value: 'pack' })}
                      disabled={isPacking}
                      icon={<Package className="h-3.5 w-3.5 text-ink-soft" />}
                      title="Mettre de côté en Pack (hors matching)"
                    >
                      Pack
                    </Button>

                    <Button
                      type="button"
                      variant="soft"
                      size="sm"
                      onClick={() => setOperatorStatusMutation.mutate({ productId: item.productId, value: 'hide' })}
                      disabled={isPacking}
                      icon={<EyeOff className="h-3.5 w-3.5 text-ink-soft" />}
                      title="Masquer le produit du site WooCommerce"
                    >
                      Masquer
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
          <div className="text-xs text-ink-soft">
            Affichage de <strong>{(page - 1) * pageSize + 1}</strong> à{' '}
            <strong>{Math.min(page * pageSize, total)}</strong> sur <strong>{total.toLocaleString('fr-FR')}</strong>{' '}
            produits
          </div>

          <div className="flex items-center gap-1.5">
            <Button
              type="button"
              variant="soft"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || approvedQuery.isFetching}
            >
              Précédent
            </Button>

            <span className="px-3 py-1 font-mono text-xs font-bold text-ink">
              Page {page} sur {totalPages}
            </span>

            <Button
              type="button"
              variant="soft"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || approvedQuery.isFetching}
            >
              Suivant
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
